import _ from 'underscore';
import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { DateSetting } from './date-setting';

const DateSettingWithCustomValidation = ({
  validationProps, updateValidation, validateOnLoad, ...restProps
}) => {
  const { data: dataSettings } = validationProps;
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const currentDate = useMemo(() => {
    const localDate = new Date(new Date().toLocaleString('en-US', { timeZone: toIANATimeZone(dataSettings.TimeZone) }));
    return new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
  }, [dataSettings.TimeZone]);
  const [isInvalidState, setIsInvalidState] = useState(false);
  const [updatedDateSettings, setUpdatedDateSettings] = useState(dataSettings);
  const validateDate = useCallback(({
    StartDate,
    EndDate,
    hasFixedStartDate = false,
  }) => {
    const validationErrors = [];
    // check wether date is valid
    if (_.isNull(StartDate) && !hasFixedStartDate) {
      validationErrors.push(i18n.getString(_TL_('Invalid start date')));
    } else if (StartDate < currentDate && !hasFixedStartDate) {
      validationErrors.push(i18n.getString(_TL_("Your start date must be after today. Today's date is calculated using your campaign time zone setting, not your actual local time.")));
    } else if (!_.isNull(EndDate) && StartDate > EndDate) {
      validationErrors.push(i18n.getString(_TL_('End date must be later than start date')));
    }
    return validationErrors;
  }, [currentDate, i18n]);

  const onChange = useCallback((newData, shouldDirty = true) => {
    const validationError = validateDate(newData);
    updateValidation(validationError, newData, shouldDirty);

    setIsInvalidState(validationError.length > 0);

    if (activity) {
      activity.signal(`onDateSettingChange - ${JSON.stringify(newData)}`);
    }
  }, [validateDate, updateValidation, activity]);

  useEffect(() => {
    setUpdatedDateSettings(_.clone(dataSettings));
  }, [dataSettings, isInvalidState]);

  useEffect(() => {
    // trigger validation on initial load
    if (validateOnLoad) {
      onChange(dataSettings, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DateSetting
      level="campaign"
      i18n={i18n}
      onChange={onChange}
      data={updatedDateSettings}
      activityLogger={activity}
      adGroupStatus={dataSettings.status}
      {...restProps}
    />
  );
};

DateSettingWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  validateOnLoad: PropTypes.bool,
  updateValidation: PropTypes.func.isRequired,
  activity: PropTypes.shape({
    signal: PropTypes.func,
  }),
};

DateSettingWithCustomValidation.defaultProps = {
  activity: null,
  validateOnLoad: false,
};

const ValidatedDateSettingInternal = withError(withCustomValidation(DateSettingWithCustomValidation));

export const ValidatedDateSetting = props => (
  <div className="validated-date-setting">
    <ValidatedDateSettingInternal {...props} />
  </div>
);
